import {
  RoomEventDto,
  SpeakingSessionRoomDto,
  SpeakingSessionRoomStructureDto,
  SpeakingSlotStatus,
  SpeakingSlotType,
} from 'api/planning';
import { SpeakingSlotAction } from './SpeakingSlotAction';
import { useIntParam, useScroll, useScrollIntoView } from 'hooks';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { GeneralError, ROUTING_PATHS } from 'const';
import { speakingSessionRoomStore, speakingSessionStore } from 'store';
import { parseServerErrors } from 'utils';
import { useErrorsContext } from 'components';
import { toast } from '@britishcouncil/react-admin';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useSpeakingSessionDetails } from '../useSpeakingSessionDetails';
import { EditRoomRouteState } from './types';
import { roundEndTimeTo20Minutes } from './utils/roundEndTimeTo20Minutes';
import { roundStartTimeTo20Minutes } from './utils/roundStartTimeTo20Minutes';
import { getMinSwaCapacity } from './utils/getMinSwaCapacity';

type Props = {
  room: SpeakingSessionRoomDto;
  roomsEvents: RoomEventDto[];
};

const noErrors: GeneralError[] = [];

export const useRoom = ({ room, roomsEvents }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const routeState = (location.state ?? {}) as EditRoomRouteState;
  const { editSpeakingSessionRoom, speakingSessionDetails, removeSpeakingSessionRoom } =
    ROUTING_PATHS;
  const { ref: gridRef } = useScroll<HTMLDivElement>('main-speaking-slots-slider');
  const { ref: scrollIntoViewRef, scrollIntoView } = useScrollIntoView<HTMLDivElement>();
  const editedRoomId = routeState.roomId;
  const sessionId = useIntParam('sessionId');
  const [expanded, setExpanded] = useState(false);
  const [roomStructures, setRoomStructures] = useState(() => room.roomStructures);
  const [savedRoomStructures, setSavedRoomStructures] = useState(() =>
    structuredClone(room.roomStructures)
  );
  const [selectedSlotAction, setSelectedSlotAction] = useState<SpeakingSlotAction>(
    SpeakingSlotAction.AddSlot
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [affectedExaminers, setAffectedExaminers] = useState<string[]>([]);
  const { setGeneralErrors } = useErrorsContext();

  const editing = room.canEdit && editedRoomId === room.id;
  const expandable = !!roomStructures.length;

  const [saveChanges, { isLoading: saving, reset }] =
    speakingSessionRoomStore.service.speakingSessionRoomApi.useEditSpeakingSessionRoomMutation({
      fixedCacheKey: editedRoomId?.toString(),
    });

  if (!editedRoomId) {
    setGeneralErrors(noErrors);
  }

  const addRoomStructure = (speakingSlotType: SpeakingSlotType) => {
    const generateTempId = () =>
      Math.min(0, roomStructures[roomStructures.length - 1]?.id ?? 0) - 1;

    setRoomStructures([
      ...roomStructures,
      {
        speakingSlotType,
        speakingSlots: [],
        id: generateTempId(),
      },
    ]);
  };

  const deleteRoomStructure = (id: number) => {
    setRoomStructures(roomStructures.filter((x) => x.id !== id));
  };

  const changeRoomStructure = (value: SpeakingSessionRoomStructureDto) => {
    setRoomStructures(roomStructures.map((item) => (item.id === value.id ? value : item)));
  };

  const handleExpandClick = () => expandable && (editing || setExpanded(!expanded));

  const performSave = async () => {
    if (editedRoomId) {
      try {
        await saveChanges({
          speakingSessionId: sessionId,
          speakingSessionRoomId: editedRoomId,
          request: {
            roomStructures: roomStructures.map((structure) => ({
              id: structure.id > 0 ? structure.id : (undefined as unknown as number),
              speakingSlotType: structure.speakingSlotType,
              speakingSlots: structure.speakingSlots.map((slot) => ({
                status: slot.status,
                startTime: slot.startTime,
                endTime: slot.endTime,
                plannedCapacity: slot.plannedCapacity,
              })),
            })),
          },
        }).unwrap();

        setSavedRoomStructures(structuredClone(roomStructures));

        toast({
          type: 'success',
          title: 'Success',
          message: 'Changes saved successfully',
        });

        goToSpeakingSessionDetails(sessionId);

        reset();
      } catch (errors) {
        const serverErrors = parseServerErrors(errors);
        setGeneralErrors(serverErrors);
      }
    }
  };

  const getInvalidSwaCapacityErrors = (): GeneralError[] => {
    return roomStructures
      .filter((structure) => structure.speakingSlotType === SpeakingSlotType.SWA)
      .filter((structure) => structure.speakingSlots.length > 0)
      .flatMap((structure) => {
        const min = getMinSwaCapacity(structure.speakingSlots);
        const max = room.capacity;
        const capacity = structure.speakingSlots[0]?.plannedCapacity ?? max;

        if (capacity < min || capacity > max) {
          return [{ message: `SWA capacity must be between ${min} and ${max}.` }];
        }

        return [];
      });
  };

  const saveRoom = () => {
    const invalidSwaCapacityErrors = getInvalidSwaCapacityErrors();

    if (invalidSwaCapacityErrors.length > 0) {
      setGeneralErrors(invalidSwaCapacityErrors);
      return;
    }

    const affectedExaminers = getAffectedExaminers(savedRoomStructures, roomStructures);

    if (affectedExaminers.length > 0) {
      setAffectedExaminers(affectedExaminers);
      setShowConfirmModal(true);
    } else {
      performSave();
    }
  };

  const confirmSave = () => {
    setShowConfirmModal(false);
    performSave();
  };

  const cancelSave = () => {
    setShowConfirmModal(false);
    setAffectedExaminers([]);
  };

  useEffect(() => {
    if (editing) {
      dispatch(
        speakingSessionStore.actions.setEditedRoomSlotCounts({
          roomId: room.id,
          counts: {
            available: roomStructures
              .flatMap((x) => x.speakingSlots)
              .filter((x) => x.status === SpeakingSlotStatus.Available)
              .reduce(
                (sum, slot) => sum + (slot.availableCapacity === 0 ? 1 : slot.availableCapacity),
                0
              ),
            awaitingApproval: roomStructures
              .flatMap((x) => x.speakingSlots)
              .filter((x) => x.status === SpeakingSlotStatus.AwaitingApproval).length,
            rejected: roomStructures
              .flatMap((x) => x.speakingSlots)
              .filter((x) => x.status === SpeakingSlotStatus.Rejected).length,
            booked: roomStructures
              .flatMap((x) => x.speakingSlots)
              .reduce((sum, slot) => sum + (slot.plannedCapacity - slot.availableCapacity), 0),
          },
        })
      );
    }
  }, [editing, roomStructures, room.id, dispatch]);

  useLayoutEffect(() => {
    if (editing) {
      setExpanded(true);
    }
  }, [editing]);

  useEffect(() => {
    if (editing) {
      const handler = setTimeout(() => {
        scrollIntoView();
      }, 100);

      return () => clearTimeout(handler);
    }
  }, [editing, scrollIntoView]);

  useEffect(() => {
    if (!editing) {
      setRoomStructures(room.roomStructures);
      setSavedRoomStructures(structuredClone(room.roomStructures));
    }
  }, [editing, room.roomStructures]);

  const goToSpeakingSessionDetails = useCallback(
    (sessionId: number) =>
      navigate(generatePath(speakingSessionDetails.absolutePath, { sessionId })),
    [navigate, speakingSessionDetails.absolutePath]
  );

  const speakingSlotTypes = useMemo(
    () => roomStructures.map((x) => x.speakingSlotType),
    [roomStructures]
  );

  const { speakingSession } = useSpeakingSessionDetails();
  const speakingSessionDate = speakingSession?.date;
  const localSpeakingSlotMinutesOffset = speakingSession?.localSpeakingSlotMinutesOffset ?? 0;

  const clashedPeriods = useMemo(() => {
    return roomsEvents
      .filter((event) => event.roomId === room.roomId)
      .flatMap((event) => ({
        startTime: roundStartTimeTo20Minutes(event.startTime, localSpeakingSlotMinutesOffset),
        endTime: roundEndTimeTo20Minutes(event.endTime, localSpeakingSlotMinutesOffset),
      }));
  }, [roomsEvents, room.roomId, localSpeakingSlotMinutesOffset]);

  const resetEditRoomMutationState = () => {
    reset();
    setGeneralErrors([]);
  };

  return {
    speakingSessionDate,
    localSpeakingSlotMinutesOffset,
    editSpeakingSessionRoom,
    removeSpeakingSessionRoom,
    gridRef,
    scrollIntoViewRef,
    speakingSlotTypes,
    roomStructures,
    clashedPeriods,
    sessionId,
    expanded,
    editing,
    saving,
    expandable,
    addRoomStructure,
    deleteRoomStructure,
    changeRoomStructure,
    saveRoom,
    handleExpandClick,
    selectedSlotAction,
    setSelectedSlotAction,
    resetEditRoomMutationState,
    showConfirmModal,
    affectedExaminers,
    confirmSave,
    cancelSave,
  };
};

export const getAffectedExaminers = (
  originalStructures: SpeakingSessionRoomStructureDto[],
  updatedStructures: SpeakingSessionRoomStructureDto[]
): string[] => {
  const affectedExaminers = new Set<string>();

  originalStructures.forEach((originalStructure) => {
    if (!originalStructure.assignedExaminer) {
      return;
    }

    const examinerName = `${originalStructure.assignedExaminer.firstName} ${originalStructure.assignedExaminer.lastName}`;
    const updatedStructure = updatedStructures.find((s) => s.id === originalStructure.id);

    if (!updatedStructure) {
      affectedExaminers.add(examinerName);
      return;
    }

    const originalSlotsSnapshot = originalStructure.speakingSlots
      .map((slot) => `${slot.startTime}-${slot.endTime}-${slot.status}`)
      .sort((a, b) => a.localeCompare(b))
      .join('|');

    const updatedSlotsSnapshot = updatedStructure.speakingSlots
      .map((slot) => `${slot.startTime}-${slot.endTime}-${slot.status}`)
      .sort((a, b) => a.localeCompare(b))
      .join('|');

    if (originalSlotsSnapshot !== updatedSlotsSnapshot) {
      affectedExaminers.add(examinerName);
    }
  });

  return Array.from(affectedExaminers);
};
